import React, { useState } from 'react';
import Hero from '../components/home/Hero';
import MspCalculator from '../components/home/MspCalculator';
import AnimateOnScroll from '../components/shared/AnimateOnScroll';
import Button from '../components/shared/Button';
import { 
  Stethoscope, Gavel, Code2, ShieldCheck, UserCog, Zap, 
  MessageSquare, UserCheck, PlugZap, Activity, TrendingUp,
  Target, BarChart3, Workflow, ChevronDown, ChevronUp,
  ArrowRight, Sparkles, Server, Cloud
} from 'lucide-react';
import './Home.css';

// Reuse service images if needed
import s1 from '../assets/images/service-1.png';
import s2 from '../assets/images/service-2.png';
import s3 from '../assets/images/service-3.png';
import s4 from '../assets/images/service-4.png';
import s5 from '../assets/images/service-5.png';

const SERVICES = [
  {
    title: 'Managed IT Services',
    description: '24/7 proactive monitoring, 15-min response SLA guarantee, and full enterprise IT support.',
    icon: Server,
    bullets: ['24/7/365 Helpdesk (L1-L3)', '15-Min Response SLA Guarantee', 'Proactive RMM & Asset Mgmt'],
    accent: 'accent-blue',
    href: '/services/managed-it',
  },
  {
    title: 'Cloud Infrastructure & DevOps',
    description: 'Architect, migrate, and optimize cloud operations across Microsoft Azure, GCP, and Hybrid Cloud.',
    icon: Cloud,
    bullets: ['Multi-Cloud Management', 'SOC2 & HIPAA Hardening', 'DevOps & CI/CD Pipelines'],
    accent: 'accent-navy',
    href: '/services/cloud-infrastructure',
  },
  {
    title: 'Managed Cybersecurity',
    description: '24/7 SOC monitoring, MDR threat detection, and SOC2/HIPAA compliance audits.',
    icon: ShieldCheck,
    bullets: ['24/7 Threat Detection', 'SOC2 & HIPAA Compliance', 'Security Vulnerability Audits'],
    accent: 'accent-light-blue',
    href: '/services/cybersecurity',
  },
  {
    title: 'AI & Process Automation',
    description: 'Robotic Process Automation (RPA), AI agents, workflow automation, and custom API integrations.',
    icon: Workflow,
    bullets: ['RPA & Intelligent Bots', 'Workflow & API Automation', '85% Cost & Time Savings'],
    accent: 'accent-blue',
    href: '/services/automation',
  },
  {
    title: 'Managed Virtual Workforce',
    description: 'Vetted dedicated virtual employees integrated into your operational workflows.',
    icon: UserCog,
    bullets: ['Dedicated Virtual Staffing', 'System Admins & Engineers', 'Back-Office Operations'],
    accent: 'accent-navy',
    href: '/services/remote-staffing',
  },
  {
    title: 'Medical BPO',
    description: 'HIPAA-compliant medical billing, coding, and clinical administrative workflows.',
    icon: Stethoscope,
    bullets: ['Medical Billing & Coding', 'Claims Processing', 'Patient Documentation'],
    accent: 'accent-blue',
    href: '/services/medical-process-outsourcing',
  },
  {
    title: 'Legal Process Outsourcing',
    description: 'Specialized legal research, document review, and compliance support workflows.',
    icon: Gavel,
    bullets: ['Legal Research', 'Document Review', 'Compliance Support'],
    accent: 'accent-light-blue',
    href: '/services/legal-process-outsourcing',
  },
];

const STEPS = [
  {
    step: 1,
    title: 'Strategic Consultation',
    description: 'We analyze your workflows and identify specific gaps where virtual talent can add the most value.',
    icon: MessageSquare,
    image: s1,
  },
  {
    step: 2,
    title: 'Specialist Matching',
    description: 'We hand-pick professionals who match your industry, time zone, and technical requirements.',
    icon: UserCheck,
    image: s2,
  },
  {
    step: 3,
    title: 'Seamless Onboarding',
    description: 'We handle the technical and administrative setup, ensuring your new team is ready on day one.',
    icon: PlugZap,
    image: s3,
  },
  {
    step: 4,
    title: 'Quality Execution',
    description: 'Continuous monitoring and reporting to ensure your KPIs are met with absolute precision.',
    icon: Activity,
    image: s4,
  },
  {
    step: 5,
    title: 'Scalable Growth',
    description: 'As your needs evolve, we scale your team and optimize processes for long-term impact.',
    icon: TrendingUp,
    image: s5,
  },
];

const PILLARS = [
  {
    title: 'Tailored Talent Pools',
    description: 'We don’t use generic job boards. Our talent is vetted specifically for the rigors of BPO and high-growth technical roles.',
    icon: Target,
  },
  {
    title: 'Measurable Outcomes',
    description: 'Every engagement starts with clear KPIs. We provide regular reports on productivity, accuracy, and cost savings.',
    icon: BarChart3,
  },
  {
    title: 'Adaptive Workflows',
    description: 'Our teams integrate directly into your tools (Slack, Jira, Salesforce), ensuring no friction in communication.',
    icon: Workflow,
  },
  {
    title: 'Uncompromising Security',
    description: 'We maintain enterprise-grade security standards, including HIPAA and GDPR compliance where required.',
    icon: ShieldCheck,
  },
];

const FAQ_ITEMS = [
  {
    question: 'How do you vet your virtual employees?',
    answer: 'We use a multi-stage vetting process including technical assessments, communication evaluations, and background checks. Only the top 2% of applicants are accepted into our talent pool.',
  },
  {
    question: 'Can I start with just one specialist?',
    answer: 'Yes! We support businesses of all sizes. You can start with a single part-time specialist and scale to a full department as your needs grow.',
  },
  {
    question: 'How do we handle time zone differences?',
    answer: 'We match talent to your specific time zone. Whether you need coverage during your business hours or 24/7 support, we align our schedules with yours.',
  },
  {
    question: 'Is there a minimum contract length?',
    answer: 'We offer flexible engagement models, including monthly rolling contracts for staffing and milestone-based agreements for development projects.',
  },
];

function Home() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <main className="home-redesign">
      <Hero />

      {/* 01. What We Offer (Our Services) */}
      <section className="home-section bg-gradient-light">
        <div className="section-container">
          <AnimateOnScroll className="section-header centered">
            <p className="section-eyebrow">Core Services</p>
            <h2 className="section-title">What we offer</h2>
            <p className="section-subtitle">
              Academic excellence and digital fluency in one place — so every business can thrive and scale beyond limits.
            </p>
          </AnimateOnScroll>

          <div className="services-modern-grid">
            {SERVICES.map((service, i) => {
              const isDark = i % 2 !== 0;
              return (
                <AnimateOnScroll 
                  key={service.title} 
                  className={`service-modern-card ${isDark ? 'dark' : 'light'}`} 
                  delay={i * 50}
                >
                  <div className={`service-icon-box ${service.accent}`}>
                    <service.icon size={28} strokeWidth={2} />
                  </div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul className="service-bullet-list">
                    {service.bullets.map(bullet => (
                      <li key={bullet}><ShieldCheck className="check-icon" size={18} /> {bullet}</li>
                    ))}
                  </ul>
                  <div className="card-footer">
                    <Button 
                      href={service.href} 
                      variant={isDark ? 'white' : 'primary'} 
                      className="btn-small"
                    >
                      Learn more
                    </Button>
                  </div>
                </AnimateOnScroll>
              );
            })}
          </div>
        </div>
      </section>

      {/* Interactive MSP ROI Calculator */}
      <MspCalculator />

      {/* 02. How It Works (Process) */}
      <section className="home-section bg-white">
        <div className="section-container">
          <AnimateOnScroll className="section-header">
            <p className="section-eyebrow">03 / How it works</p>
            <h2 className="section-title">A structured journey to <br /><span className="text-blue">measurable progress</span></h2>
            <p className="section-subtitle">
              Five clear steps — consultation, matching, and onboarding designed around real business needs and real timelines.
            </p>
          </AnimateOnScroll>

          <div className="process-grid-container">
            {STEPS.map((step, i) => (
              <AnimateOnScroll key={step.step} className="process-vertical-card" delay={i * 100}>
                <div className="process-card-image-box">
                  <img src={step.image} alt={step.title} />
                  <div className="step-badge-new">{String(step.step).padStart(2, '0')}</div>
                  <div className="step-icon-new">
                    <step.icon size={20} />
                  </div>
                </div>
                <div className="process-card-text">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                  <div className="process-card-footer">
                    <Button href="/get-started" variant="outline" className="btn-small">
                      Learn more
                    </Button>
                  </div>
                </div>
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* 03. Personalised Learning (Operational Excellence) */}
      <section className="home-section bg-navy text-white">
        <div className="section-container">
          <div className="split-layout">
            <div className="split-content">
              <AnimateOnScroll>
                <p className="section-eyebrow text-blue">04 / Operational Excellence</p>
                <h2 className="section-title white">Business-centred support, <br /><span className="text-light-blue">results-driven growth</span></h2>
                <p className="section-subtitle white">
                  VistaHand pairs technical rigour with a human pace — so your team feels heard, challenged appropriately, and supported between milestones.
                </p>
                <div style={{ marginTop: '40px' }}>
                  <Button href="/contact" variant="primary">
                    Discuss your scaling goals <ArrowRight size={20} style={{ marginLeft: '10px' }} />
                  </Button>
                </div>
              </AnimateOnScroll>
            </div>
            <div className="split-grid">
              {PILLARS.map((pillar, i) => (
                <AnimateOnScroll key={pillar.title} className="pillar-card" delay={i * 50}>
                  <div className="pillar-icon"><pillar.icon size={24} /></div>
                  <h4>{pillar.title}</h4>
                  <p>{pillar.description}</p>
                </AnimateOnScroll>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 04. Platform (Stats Marquee) */}
      <section className="marquee-section">
        <div className="marquee-track">
          <div className="marquee-content">
            <span>SMART SCHEDULING <Sparkles size={20} /></span>
            <span>TALENT MATCHING <Sparkles size={20} /></span>
            <span>LIVE SUPPORT <Sparkles size={20} /></span>
            <span>KPI DASHBOARDS <Sparkles size={20} /></span>
            <span>HIPAA COMPLIANT <Sparkles size={20} /></span>
            <span>COST SAVINGS <Sparkles size={20} /></span>
          </div>
          <div className="marquee-content">
            <span>SMART SCHEDULING <Sparkles size={20} /></span>
            <span>TALENT MATCHING <Sparkles size={20} /></span>
            <span>LIVE SUPPORT <Sparkles size={20} /></span>
            <span>KPI DASHBOARDS <Sparkles size={20} /></span>
            <span>HIPAA COMPLIANT <Sparkles size={20} /></span>
            <span>COST SAVINGS <Sparkles size={20} /></span>
          </div>
        </div>
      </section>

      {/* 05. FAQ Section */}
      <section className="home-section bg-white" id="faq">
        <div className="section-container">
          <div className="faq-layout">
            <div className="faq-info">
              <AnimateOnScroll>
                <p className="section-eyebrow">06 / FAQ</p>
                <h2 className="section-title">Frequently asked questions</h2>
                <p className="section-subtitle">
                  Quick answers about how VistaHand supports learners and organisations — from getting started to security and help.
                </p>
                <div style={{ marginTop: '32px' }}>
                  <Button href="/contact" variant="outline">
                    See all FAQs <ArrowRight size={18} style={{ marginLeft: '8px' }} />
                  </Button>
                </div>
              </AnimateOnScroll>
            </div>
            <div className="faq-list">
              {FAQ_ITEMS.map((item, index) => (
                <div 
                  key={index} 
                  className={`faq-item ${openFaq === index ? 'active' : ''}`}
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <div className="faq-question">
                    <span>{item.question}</span>
                    <div className={`faq-icon ${openFaq === index ? 'active' : ''}`}>
                      {openFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </div>
                  <div className="faq-answer">
                    <p>{item.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 06. Final CTA */}
      <section className="home-section">
        <div className="section-container">
          <AnimateOnScroll className="final-cta-box bg-navy">
            <div className="cta-overlay"></div>
            <div className="cta-content centered text-white">
              <div className="cta-badge">
                <span className="dot"></span>
                Getting closer with us
              </div>
              <h2 className="section-title white">Start today and unlock your <br /><span className="text-light-blue">business potential</span></h2>
              <p className="section-subtitle white">
                Book a quick call or send an inquiry. We’ll map the right pathway — medical BPO, digital skills, or technical staffing — then help you plan sessions that fit your schedule.
              </p>
              <div className="cta-actions">
                <Button href="/contact" variant="primary">
                  Start your journey <ArrowRight size={20} style={{ marginLeft: '10px' }} />
                </Button>
                <Button href="/services" variant="outline" className="btn-white">
                  Explore all services <Sparkles size={20} style={{ marginLeft: '10px' }} />
                </Button>
              </div>
            </div>
          </AnimateOnScroll>
        </div>
      </section>
    </main>
  );
}

export default Home;
